import { GuideTiming, type GuideBlock, type GuideHost, type GuideStep } from "./guide";
import type { ActiveView } from "./guideQueue";
import { GuideLayout, catchers, worldHole } from "./guideGeometry";
import { PAUSED_CHIP_SPACE, bands, drawGlove, locate, placeWords, put, px, resolveTarget, type ResolvedTarget } from "./guideDom";
import { setGuideSurface } from "./guideSurface";
import { buildGlove, el, preloadPoses, setMarkedText, type BubbleParts, type TipParts, type WelcomeParts } from "./guideWidgets";
import { buildStepCard, mountStepBram } from "./guideCards";
import type { BramHandle } from "./bram/bramView";
import { Typewriter } from "./typewriter";
import { motionOk } from "../kit";

/**
 * The guide's overlay (DESIGN §3): one `#guide` root at z 25 in the screen's UI root, holding the spotlight (a hole
 * whose shadow is the dim, a breathing gold ring, four tap catchers for a hard block), the glove, and the step's
 * bubble, tip or welcome card. It draws what guideController.ts tells it and reports taps back; it decides nothing
 * about steps. Placement maths is in guideGeometry.ts.
 */

export interface GuideViewHandlers {
  /** A tap anywhere while an info step is up (the queue applies the tap guard). */
  tap(): void;
  /** The welcome card's CTA, or its alt link. */
  cta(alt: boolean): void;
  /** A tip's ✕. */
  close(): void;
  /** "Skip tips", confirmed. */
  skip(): void;
}

export interface GuideView {
  show(step: GuideStep, host: GuideHost, skip: false | "walkthrough" | "hints"): void;
  refresh(v: ActiveView): void;
  clear(): void;
}

/** After a step shows, follow a DOM target every frame this long: cards deal in and trays slide up. */
const SETTLE_MS = 900;
/** Between frames, re-measure at most this often (the controller refreshes after every sim change). */
const RELAYOUT_MS = 100;
/** The drain moves in tenths when motion is reduced. */
const DRAIN_STEPS = 10;
/** A finished step's card shrinks away this long (guide.css `g-leave`) unless the next one is already up. */
const LEAVE_MS = 180;
/**
 * The entrance (guide.css `#guide.enter`: g-pop, g-rise, g-fade, g-hero-in, all ≤ 0.5 s) is over by then, and the class comes off: a root that
 * is re-appended (a screen re-rendering its markup) or un-hidden (back from aside) must not pop in a second time
 * (M45, "looks like it double renders").
 */
const ENTER_MS = 1500;
/**
 * A target lost for less than this keeps its last spotlight (owner 2026-09-30, flicker): a world anchor slipping under
 * the HUD for a frame as the camera eases, a raider swapped for the next one, a card re-dealt. The hole used to vanish
 * and, on a hard block, the whole-screen dim to flash up in its place, frame by frame.
 */
export const TARGET_GRACE_MS = 700;

const defaultBlock = (step: GuideStep): GuideBlock => step.block ?? (step.kind === "tip" ? "none" : "hard");

interface Shown {
  readonly step: GuideStep;
  host: GuideHost;
  readonly block: GuideBlock;
  readonly bubble: BubbleParts | null;
  readonly tip: TipParts | null;
  readonly welcome: WelcomeParts | null;
  readonly since: number;
  aside: boolean;
  lastLayout: number;
  key: string;
  described: HTMLElement | null;
  observed: HTMLElement | null;
  /** The captain in the bubble or on the welcome card (live or still). */
  readonly bram: BramHandle | null;
  /** The line being typed (bubble, tip, welcome). */
  readonly typer: Typewriter | null;
  /** When the step last became visible (shown, or back from aside): a tap sooner than the guard does nothing. */
  visibleAt: number;
  /** The queue's "Tap to continue" cue, shown once the line is typed out. */
  cue: boolean;
  /** The last target found, and when (TARGET_GRACE_MS). */
  lastFound: ResolvedTarget | null;
  lastFoundAt: number;
}

export function createOverlay(h: GuideViewHandlers): GuideView {
  preloadPoses();
  const root = el("div", "");
  root.id = "guide";
  const dim = el("div", "g-dim");
  const hole = el("div", "g-hole");
  const ring = el("div", "g-ring");
  const ring2 = el("div", "g-ring thin round");
  const catch4 = [0, 1, 2, 3].map(() => el("div", "g-catch"));
  const glove = buildGlove();
  const paused = el("div", "g-paused", "❚❚ Paused");
  let shown: Shown | null = null;
  let raf = 0;
  let confirmOpen = false;
  let leaving: { el: HTMLElement; timer: number } | null = null;
  let enterTimer = 0;
  const ro = typeof ResizeObserver === "function" ? new ResizeObserver(() => layout(true)) : null;

  // A catcher stops the tap (a hard block); on a `do` step it nudges the bubble and re-pulses the glove. Never skips.
  const swallow = (e: Event) => { e.stopPropagation(); if (e.cancelable && e.type !== "pointerdown") e.preventDefault(); };
  for (const c of [...catch4, dim]) {
    c.addEventListener("pointerdown", swallow);
    c.addEventListener("click", (e) => { swallow(e); if (shown?.step.kind === "do") nudge(); });
  }

  // An info step continues on a tap anywhere, the hole included (the target still gets its tap); the guide's own
  // controls (the chip, its confirm) do not count.
  const onAnyClick = (e: Event) => {
    if (!shown || shown.aside || shown.step.kind !== "info" || confirmOpen) return;
    const t = e.target as Element | null;
    if (t && typeof t.closest === "function" && t.closest(".g-skip, .g-confirm")) return;
    // the first tap on a line still typing shows the rest; the next one continues (the tap guard holds for both)
    if (shown.typer?.running) {
      if (performance.now() - shown.visibleAt >= GuideTiming.TapGuardMs) shown.typer.finish();
      return;
    }
    h.tap();
  };
  const onResize = () => layout(true);
  // M45: the target moves without the window resizing: a sheet scrolls, the fonts land, Larger text re-flows the HUD.
  const onScroll = () => layout(true);
  const onFonts = () => layout(true);

  function nudge(): void {
    shown?.bram?.nudge();
    const w = shown?.bubble?.wrap;
    if (!w || !motionOk()) return;
    w.classList.remove("shake");
    void w.offsetWidth;
    w.classList.add("shake");
    window.setTimeout(() => w.classList.remove("shake"), GuideTiming.NudgeMs);
    glove.hand.classList.remove("pulse");
    void glove.hand.offsetWidth;
    glove.hand.classList.add("pulse");
  }

  function wantsFrames(s: Shown): boolean {
    const t = s.step.target;
    return (t !== undefined && "world" in t) || s.step.ring !== undefined || performance.now() - s.since < SETTLE_MS;
  }

  function loop(): void {
    raf = 0;
    if (!shown || shown.aside) return;
    layout(true);
    if (wantsFrames(shown)) raf = requestAnimationFrame(loop);
  }
  function startLoop(): void { if (!raf && typeof requestAnimationFrame === "function") raf = requestAnimationFrame(loop); }
  function stopLoop(): void { if (raf) cancelAnimationFrame(raf); raf = 0; }

  /** Measure and place everything; skipped when nothing moved. */
  function layout(force: boolean): void {
    const s = shown;
    if (!s || s.aside) return;
    const t = performance.now();
    if (!force && t - s.lastLayout < RELAYOUT_MS) return;
    s.lastLayout = t;
    const rr = root.getBoundingClientRect();
    const vw = rr.width, vh = rr.height, ox = rr.left, oy = rr.top;
    const step = s.step;
    const target = step.target;

    // Resolve the target: the spotlight (hole) and where the glove points; a target lost a moment ago keeps its place.
    let resolved = resolveTarget(step, s.host, ox, oy, vw, vh);
    if (resolved.holeBox || resolved.pointBox) { s.lastFound = resolved; s.lastFoundAt = t; }
    else if (s.lastFound && t - s.lastFoundAt < TARGET_GRACE_MS) resolved = s.lastFound;
    const { holeBox, pointBox, world, targetEl } = resolved;
    // the captain looks at what the glove points at (or the spotlight), in viewport px
    const lookBox = pointBox ?? holeBox;
    s.bram?.look(lookBox ? { x: lookBox.x + lookBox.w / 2 + ox, y: lookBox.y + lookBox.h / 2 + oy } : null);
    const ringAnchor = step.ring ? locate(s.host, step.ring) : null;
    const ring2Box = ringAnchor ? worldHole(ringAnchor, 1) : null;

    // A `do` step whose target is not on screen yet must not trap the player behind a dim: no block until it is.
    let block = s.block;
    if (!holeBox && target && step.kind === "do") block = "none";
    const b = s.welcome ? null : bands(oy, vh);
    const topBand = b ? b.top + (step.holdSim ? PAUSED_CHIP_SPACE : 0) : 0;
    const bubbleH = s.bubble ? s.bubble.bubble.offsetHeight : s.tip ? s.tip.tip.offsetHeight : 0;

    const key = [vw, vh, holeBox?.x, holeBox?.y, holeBox?.w, holeBox?.h, pointBox?.x, pointBox?.y, pointBox?.w, pointBox?.h, ring2Box?.x, ring2Box?.y, ring2Box?.w, b?.top, b?.bottom, bubbleH, block]
      .map((v) => (typeof v === "number" ? Math.round(v) : String(v))).join(",");
    if (key === s.key) return;
    s.key = key;

    // Spotlight and dim.
    const dimmed = block === "hard" || block === "soft";
    hole.className = `g-hole${block === "soft" ? " soft" : ""}${world ? " round" : ""}`;
    put(hole, dimmed ? holeBox : null);
    dim.className = `g-dim${block === "soft" ? " soft" : ""}`;
    dim.hidden = !(block === "hard" && !holeBox) || s.welcome !== null;
    ring.className = `g-ring${world ? " round" : ""}${block === "none" ? " thin" : ""}`;
    put(ring, holeBox);
    put(ring2, ring2Box);
    const cs = block === "hard" && holeBox ? catchers(holeBox, vw, vh) : null;
    catch4.forEach((c, i) => put(c, cs ? cs[i] : null));

    // The glove.
    drawGlove(glove, (step.pointer ?? (step.kind === "do" && target !== undefined)) ? pointBox : null, world, vw, vh);

    paused.hidden = !step.holdSim;
    if (b && step.holdSim) paused.style.top = px(b.top + GuideLayout.BandGap / 2);

    // The words.
    const topClear = b ? placeWords({ step, bubble: s.bubble, tip: s.tip, vw, vh, oy, bubbleH, target: resolved, topBand, bottomBand: b.bottom }) : null;
    setGuideSurface({ blocking: step.kind !== "tip", topClear });
    if (typeof document !== "undefined") document.documentElement.style.setProperty("--guide-clear", topClear === null ? "" : px(topClear));

    // The target is described by the bubble while the step is up; a DOM target is watched for size changes.
    if (targetEl !== s.described) {
      s.described?.removeAttribute("aria-describedby");
      if (targetEl && s.bubble) targetEl.setAttribute("aria-describedby", "g-text");
      s.described = targetEl;
    }
    if (ro && targetEl !== s.observed) { if (s.observed) ro.unobserve(s.observed); if (targetEl) ro.observe(targetEl); s.observed = targetEl; }
  }

  function dropLeaving(): void {
    if (!leaving) return;
    window.clearTimeout(leaving.timer);
    leaving.el.remove();
    leaving = null;
  }

  /** Takes the step down; `leave` lets its card shrink away first (nothing else of the step stays: no dim, no taps). */
  function teardown(leave: boolean): void {
    stopLoop();
    dropLeaving();
    const card = shown && !shown.aside && root.isConnected ? shown.bubble?.wrap ?? shown.tip?.tip ?? shown.welcome?.root ?? null : null;
    if (shown) {
      shown.described?.removeAttribute("aria-describedby");
      if (ro && shown.observed) ro.unobserve(shown.observed);
      shown.typer?.stop();
      shown.bram?.release(leave && card !== null);
    }
    shown = null;
    confirmOpen = false;
    if (leave && card) {
      card.classList.add("g-leave");
      card.setAttribute("inert", "");
      card.setAttribute("aria-hidden", "true");
      root.replaceChildren(card);
      leaving = { el: card, timer: window.setTimeout(() => { leaving = null; if (!shown) { root.replaceChildren(); root.remove(); } }, LEAVE_MS) };
    } else {
      root.replaceChildren();
      root.remove();
    }
    window.removeEventListener("click", onAnyClick, true);
    window.removeEventListener("resize", onResize);
    window.removeEventListener("scroll", onScroll, true);
    window.visualViewport?.removeEventListener("resize", onResize);
    document.fonts?.removeEventListener?.("loadingdone", onFonts);
    window.clearTimeout(enterTimer);
    setGuideSurface(null);
    document.documentElement.style.removeProperty("--guide-clear");
  }

  const clear = (): void => teardown(motionOk());

  function show(step: GuideStep, host: GuideHost, skip: false | "walkthrough" | "hints"): void {
    // The same step again (its screen re-attached on a new root): move the one card over, never build a second or
    // replay its entrance and typing (M45: one tip DOM, one captain, no re-mount).
    if (shown && shown.step === step) {
      shown.host = host;
      if (root.parentNode !== host.root) host.root.append(root);
      shown.key = "";
      layout(true);
      return;
    }
    teardown(false);
    const block = defaultBlock(step);
    root.className = `g-${step.kind}${motionOk() ? " enter" : ""}`;
    enterTimer = window.setTimeout(() => root.classList.remove("enter"), ENTER_MS);
    const card = buildStepCard(step, skip, { cta: h.cta, close: h.close, skip: h.skip, confirming: (on) => { confirmOpen = on; } });
    const { bubble, tip, welcome } = card;
    if (!welcome) root.append(dim, hole, ring, ring2, ...catch4, paused, glove.ripple, glove.hand);
    root.append(card.el);
    for (const e of [hole, ring, ring2, glove.hand, glove.ripple, paused]) e.hidden = true;
    dim.hidden = true;
    root.hidden = false;
    host.root.append(root);
    // the captain goes in once the card is in the document (he only draws where he can be seen)
    const bram = mountStepBram(step, card);
    const words = bubble?.text ?? tip?.text ?? welcome?.text ?? null;
    const typer = words ? new Typewriter(words, step.kind, (sp) => bram?.say(sp), () => showCue()) : null;
    // a tap on the welcome card (not its buttons) shows the rest of the line at once
    welcome?.root.addEventListener("click", (e) => { if (!(e.target as Element | null)?.closest?.("button")) typer?.finish(); });
    const now = performance.now();
    shown = { step, host, block, bubble, tip, welcome, since: now, aside: false, lastLayout: -Infinity, key: "", described: null, observed: null, bram, typer, visibleAt: now, cue: false, lastFound: null, lastFoundAt: -Infinity };
    window.addEventListener("click", onAnyClick, true);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    window.visualViewport?.addEventListener("resize", onResize);
    document.fonts?.addEventListener?.("loadingdone", onFonts);
    layout(true);
    startLoop();
    welcome?.cta.focus({ preventScroll: true });
  }

  /** "Tap to continue" once the queue says it is due and the line is typed out. */
  function showCue(): void {
    const s = shown;
    s?.bubble?.cue.classList.toggle("on", s.cue && !s.typer?.running);
  }

  function refresh(v: ActiveView): void {
    const s = shown;
    if (!s || v.step !== s.step) return;
    if (v.aside !== s.aside) {
      s.aside = v.aside;
      root.hidden = v.aside;
      if (v.aside) { stopLoop(); setGuideSurface(null); } else { s.visibleAt = performance.now(); s.key = ""; layout(true); startLoop(); }
    }
    if (v.aside) return;
    // A screen that rebuilds its markup (the hub's render) drops the overlay: put it back.
    if (root.parentNode !== s.host.root) { s.host.root.append(root); s.key = ""; }
    const text = s.bubble?.text ?? s.tip?.text ?? s.welcome?.text;
    const changed = text !== undefined && text.dataset.text !== v.text;
    if (s.typer) s.typer.set(v.text);
    else if (text) setMarkedText(text, v.text);
    s.cue = v.cue;
    showCue();
    s.bram?.poke();
    if (s.tip && v.drain !== null) {
      const d = motionOk() ? v.drain : Math.ceil(v.drain * DRAIN_STEPS) / DRAIN_STEPS;
      s.tip.drain.style.width = `${(d * 100).toFixed(1)}%`;
    }
    layout(changed);
    if (!raf && wantsFrames(s)) startLoop();
  }

  return { show, refresh, clear };
}
